import React, { useState, useEffect } from "react";
import { User, Mail, Ticket, LogOut, Calendar, MapPin, QrCode } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Separate component to avoid nested map issues with babel plugin
const OrderCard = ({ order }) => {
  const items = order.items || [];
  
  return (
    <div
      className="glass-card rounded-lg p-4"
      data-testid={`user-order-${order.id}`}
    >
      {items.map((item, idx) => (
        <div key={idx} className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
            <QrCode className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm truncate">
              {item.event_title}
            </h4>
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {item.event_date}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {item.event_venue}
              </span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                {item.ticket_type} × {item.quantity}
              </span>
              <span className="text-sm font-bold text-primary">
                {(item.price * item.quantity).toFixed(2)}€
              </span>
            </div>
          </div>
        </div>
      ))}
      <div className="mt-3 pt-3 border-t border-border/50 flex justify-between items-center">
        <span className="text-xs text-muted-foreground">
          Commande #{order.id.slice(0, 8)}
        </span>
        <span className="text-xs text-muted-foreground">
          Total: <span className="font-bold text-foreground">{order.total?.toFixed(2)}€</span>
        </span>
      </div>
    </div>
  );
};

export const UserProfile = ({ isOpen, onClose, user, onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && user) {
      fetchUserOrders();
    }
  }, [isOpen, user]);

  const fetchUserOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("tika_user_token");
      const response = await axios.get(`${API}/auth/my-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data || []);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors du chargement de vos commandes");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("tika_user_token");
    localStorage.removeItem("tika_user");
    toast.success("Déconnexion réussie");
    onLogout();
    onClose();
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Mon compte</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 mt-4">
          {/* User Info */}
          <div className="glass-card rounded-lg p-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                <User className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h3 className="font-bold text-lg">{user.name}</h3>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  {user.email}
                </p>
              </div>
            </div>
          </div>

          {/* Orders Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Ticket className="w-5 h-5 text-primary" />
              <h3 className="font-bold text-lg">Mes billets</h3>
            </div>

            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                Chargement...
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-8 glass-card rounded-lg">
                <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Vous n'avez pas encore de billets</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Réservez vos premiers billets dès maintenant !
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((order) => (
                  <OrderCard key={order.id} order={order} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Logout Button */}
        <div className="pt-4 border-t border-border">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full"
            data-testid="user-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Se déconnecter
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
